import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/api/client";
import { listStrategies } from "@/api/alpha";

type TabKey = "basic" | "stockpool" | "risk" | "prompt";
const TABS: { key: TabKey; label: string }[] = [
  { key: "basic", label: "基本信息" }, { key: "stockpool", label: "股票池" },
  { key: "risk", label: "资金风控" }, { key: "prompt", label: "AI提示词" },
];
const STYLES = ["稳健型", "平衡型", "激进型", "事件驱动", "指数跟踪", "自由"] as const;
const POOLS = ["沪深300", "中证500", "中证1000", "创业板", "科创板", "北交所"];
const SECTORS = ["科技", "新能源", "消费", "医药", "金融", "军工", "房地产", "能源", "材料", "通信"];
const STYLE_KEY: Record<string, string> = { "稳健型": "stable", "平衡型": "balanced", "激进型": "aggressive", "事件驱动": "event", "指数跟踪": "index", "自由": "ai" };
const RISK_TEMPLATE: Record<string, any> = {
  "稳健型": { maxPos: 15, conc: 30, cash: 30, profit: 5, loss: 3, dd: 8 },
  "平衡型": { maxPos: 20, conc: 30, cash: 20, profit: 8, loss: 5, dd: 12 },
  "激进型": { maxPos: 25, conc: 40, cash: 10, profit: 8, loss: 5, dd: 18 },
  "事件驱动": { maxPos: 20, conc: 35, cash: 25, profit: 12, loss: 6, dd: 15 },
  "指数跟踪": { maxPos: 8, conc: 25, cash: 15, profit: 0, loss: 10, dd: 20 },
  "自由": { maxPos: 25, conc: 40, cash: 15, profit: 10, loss: 6, dd: 15 },
};
const DEFAULT_PROMPTS: Record<string, string> = {
  debate: "本策略以稳健投资为核心...", fundamental: "重点核查：营收增速、毛利率变化、ROE连续性...",
  technical: "买入信号：RSI<35。卖出信号：RSI>65。", sentiment: "重点输出：北向资金板块流向...",
  news: "重点扫描：退市相关公告、立案调查...", bull: "必须给出具体的数字证据支持看多观点。",
  bear: "重点提示风险：集中度风险、相关性风险。",
};

export default function AIStrategyCreate() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const fromSlug = searchParams.get("from");
  const editId = searchParams.get("edit") ? Number(searchParams.get("id")) : null;

  const [saving, setSaving] = useState(false);
  const [savingAsDraft, setSavingAsDraft] = useState(false);
  const [tab, setTab] = useState<TabKey>("basic");
  const [loadingFrom, setLoadingFrom] = useState(!!fromSlug || !!editId);

  const [name, setName] = useState(searchParams.get("name") || "");
  const [style, setStyle] = useState(searchParams.get("style") ? STYLE_KEY[searchParams.get("style")!] ? Object.entries(STYLE_KEY).find(([, v]) => v === searchParams.get("style"))?.[0] || "平衡型" : "平衡型" : "平衡型");
  const [tags, setTags] = useState(searchParams.get("tags") || "");
  const [desc, setDesc] = useState(searchParams.get("desc") || "");
  const [holdDays, setHoldDays] = useState(Number(searchParams.get("holdDays")) || 30);

  const [pools, setPools] = useState<string[]>(searchParams.get("pools")?.split(",") || ["沪深300", "中证500"]);
  const [sectorsAllow, setSectorsAllow] = useState<string[]>(searchParams.get("sectors")?.split(",") || ["科技", "新能源"]);
  const [sectorsDeny, setSectorsDeny] = useState<string[]>([]);
  const [peMin, setPeMin] = useState(searchParams.get("peMin") || "");
  const [peMax, setPeMax] = useState(searchParams.get("peMax") || "30");
  const [capMin, setCapMin] = useState(searchParams.get("capMin") || "100");
  const [capMax, setCapMax] = useState(searchParams.get("capMax") || "");
  const [maxHoldings, setMaxHoldings] = useState(Number(searchParams.get("maxHoldings")) || 5);
  const [maxPos, setMaxPos] = useState(Number(searchParams.get("maxPos")) || 20);
  const [conc, setConc] = useState(Number(searchParams.get("conc")) || 30);
  const [cashFloor, setCashFloor] = useState(Number(searchParams.get("cashFloor")) || 20);
  const [profitPct, setProfitPct] = useState(Number(searchParams.get("profitPct")) || 8);
  const [lossPct, setLossPct] = useState(Number(searchParams.get("lossPct")) || 5);
  const [maxDD, setMaxDD] = useState(Number(searchParams.get("maxDD")) || 12);
  const [customPrompt, setCustomPrompt] = useState(false);
  const [prompts, setPrompts] = useState<Record<string, string>>({ ...DEFAULT_PROMPTS });
  const [aiBusy, setAiBusy] = useState<string | null>(null);

  const pval = (key: string, def: string) => searchParams.get(key) || def;

  // Only load from API if fromSlug is set (edit mode already parsed from URL params)
  useEffect(() => {
    if (!fromSlug) { setLoadingFrom(false); return; }
    (async () => {
      try {
        const [{ strategies }] = await Promise.all([listStrategies()]);
        const src = strategies.find((s: any) => s.slug === fromSlug);
        if (src) {
          setName(src.name + " (复制)");
          setStyle(Object.entries(STYLE_KEY).find(([, v]) => v === src.style)?.[0] || "平衡型");
          setTags(src.tags || "");
          setDesc(src.description || "");
          try {
            const detail = await api.get<any>(`/alpha-lab/strategies/${src.id}`);
            const cfg = JSON.parse(detail.configParams || "{}");
            if (cfg.stockPool?.pools) setPools(cfg.stockPool.pools);
            if (cfg.stockPool?.industriesAllow) setSectorsAllow(cfg.stockPool.industriesAllow);
            if (cfg.capital) { setMaxPos(cfg.capital.maxSinglePosPct ?? 20); setConc(cfg.capital.maxSectorConcPct ?? 30); setCashFloor(cfg.capital.cashFloorPct ?? 20); }
            if (cfg.risk) { setProfitPct(cfg.risk.takeProfitPct ?? 8); setLossPct(cfg.risk.stopLossPct ?? 5); setMaxDD(cfg.risk.maxDrawdownPct ?? 12); }
          } catch {}
        }
      } catch {}
      setLoadingFrom(false);
    })();
  }, [fromSlug]);

  const buildConfig = () => ({
    stockPool: { pools: pools.filter(Boolean), industriesAllow: sectorsAllow.filter(Boolean), peMin: peMin ? Number(peMin) : undefined, peMax: peMax ? Number(peMax) : undefined, totalCapMinYi: capMin ? Number(capMin) : undefined, totalCapMaxYi: capMax ? Number(capMax) : undefined, maxHoldings },
    capital: { maxSinglePosPct: maxPos, maxSectorConcPct: conc, cashFloorPct: cashFloor },
    risk: { takeProfitPct: profitPct, stopLossPct: lossPct, maxDrawdownPct: maxDD },
  });

  const handleSave = async (asDraft = false) => {
    if (!name.trim()) { alert("策略名称必填"); return; }
    setSaving(true);
    try {
      const payload = {
        name: name.trim(), style: STYLE_KEY[style] || "balanced",
        tags: tags.split(",").map(s => s.trim()).filter(Boolean).join(","),
        description: desc, visibility: "private",
        configParams: JSON.stringify(buildConfig()),
        holdingPeriodDays: holdDays,
      };
      const status = asDraft ? 4 : 1;
      Object.assign(payload, { status });
      if (editId) { await api.put(`/alpha-lab/strategies/${editId}`, payload); }
      else { await api.post("/alpha-lab/strategies", payload); }
      navigate("/ai-strategy");
    } catch (e) { alert(e instanceof Error ? e.message : "保存失败"); }
    finally { setSaving(false); }
  };

  const applyRiskTemplate = (s: string) => {
    const t = RISK_TEMPLATE[s]; if (!t) return;
    setMaxPos(t.maxPos); setConc(t.conc); setCashFloor(t.cash);
    setProfitPct(t.profit); setLossPct(t.loss); setMaxDD(t.dd);
  };

  const handleAIRewrite = async (key: string, current: string) => {
    setAiBusy(key);
    await new Promise(r => setTimeout(r, 500));
    setPrompts(prev => ({ ...prev, [key]: current + "\n\n【AI优化】增加了更具体的参数约束和风险控制规则。" }));
    setAiBusy(null);
  };

  const handleAIRecommendRisk = async () => {
    const t = RISK_TEMPLATE[style]; if (!t) return;
    await new Promise(r => setTimeout(r, 300));
    setMaxPos(t.maxPos); setConc(t.conc); setCashFloor(t.cash);
    setProfitPct(t.profit); setLossPct(t.loss); setMaxDD(t.dd);
  };

  const inputCls = "w-full bg-background-100 rounded-xl px-4 py-2.5 text-sm text-foreground-950 placeholder:text-foreground-300 outline-none border-none";
  const togglePool = (p: string) => setPools(prev => prev.includes(p) ? prev.filter(x => x !== p) : [...prev, p]);
  const toggleSector = (list: string[], set: (v: string[]) => void, s: string) => set(list.includes(s) ? list.filter(x => x !== s) : [...list, s]);

  if (loadingFrom) return <div className="flex-1 flex items-center justify-center"><div className="w-5 h-5 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="flex-1 flex flex-col">
      <div className="px-4 pt-4 pb-3 flex items-center justify-between border-b border-background-100">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="w-8 h-8 rounded-full bg-background-100 flex items-center justify-center cursor-pointer"><i className="ri-arrow-left-s-line text-foreground-600 text-lg" /></button>
          <h1 className="text-lg font-bold text-foreground-950">{editId ? "编辑策略" : fromSlug ? "复制策略" : "新建策略"}</h1>
        </div>
        <div className="flex gap-2">
          <button onClick={() => handleSave(true)} disabled={saving}
            className="px-3 py-1.5 rounded-lg text-xs font-medium bg-background-100 text-foreground-600 cursor-pointer disabled:opacity-50">{saving ? "保存中..." : "存草稿"}</button>
          <button onClick={() => handleSave(false)} disabled={saving}
            className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-primary-500 text-white cursor-pointer disabled:opacity-50">{saving ? "保存中..." : editId ? "更新" : "提交"}</button>
        </div>
      </div>
      <div className="flex border-b border-background-100 overflow-x-auto scrollbar-hide">
        {TABS.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className="px-4 py-3 text-xs font-medium cursor-pointer whitespace-nowrap transition-all"
            style={{ borderBottom: tab === t.key ? "2px solid #6366f1" : "2px solid transparent", color: tab === t.key ? "#6366f1" : "#94a3b8" }}>{t.label}</button>
        ))}
      </div>
      <div className="flex-1 overflow-y-auto px-4 py-4">
        {tab === "basic" && (
          <div className="space-y-4">
            <div><label className="text-xs font-medium text-foreground-600 mb-1.5 block">策略名称</label><input value={name} onChange={e => setName(e.target.value)} placeholder="例如：我的稳健策略 v1" className={inputCls} /></div>
            <div><label className="text-xs font-medium text-foreground-600 mb-1.5 block">策略风格</label><div className="flex flex-wrap gap-1.5">{STYLES.map(s => (<button key={s} onClick={() => { setStyle(s); applyRiskTemplate(s); }} className={`text-[11px] px-3 py-1.5 rounded-full font-medium cursor-pointer transition-colors ${style === s ? "bg-primary-500 text-white" : "bg-background-100 text-foreground-500"}`}>{s}</button>))}</div></div>
            <div><label className="text-xs font-medium text-foreground-600 mb-1.5 block">标签</label><input value={tags} onChange={e => setTags(e.target.value)} placeholder="红利,价值,低波动" className={inputCls} /></div>
            <div><label className="text-xs font-medium text-foreground-600 mb-1.5 block">描述</label><textarea value={desc} onChange={e => setDesc(e.target.value)} placeholder="简要描述策略逻辑..." rows={3} className={inputCls + " resize-none"} /></div>
            <div><label className="text-xs font-medium text-foreground-600 mb-1.5 block">持有周期（天）</label><input type="number" value={holdDays} onChange={e => setHoldDays(Number(e.target.value))} className={inputCls} /></div>
          </div>
        )}
        {tab === "stockpool" && (
          <div className="space-y-4">
            <div><label className="text-xs font-medium text-foreground-600 mb-1.5 block">股票池</label><div className="flex flex-wrap gap-1.5">{POOLS.map(p => (<button key={p} onClick={() => togglePool(p)} className={`text-[11px] px-3 py-1.5 rounded-full font-medium cursor-pointer ${pools.includes(p) ? "bg-primary-500 text-white" : "bg-background-100 text-foreground-500"}`}>{p}</button>))}</div></div>
            <div><label className="text-xs font-medium text-foreground-600 mb-1.5 block">行业偏好</label><div className="flex flex-wrap gap-1.5">{SECTORS.map(s => (<button key={s} onClick={() => toggleSector(sectorsAllow, setSectorsAllow, s)} className={`text-[11px] px-3 py-1.5 rounded-full font-medium cursor-pointer ${sectorsAllow.includes(s) ? "bg-green-500 text-white" : "bg-background-100 text-foreground-500"}`}>{s}</button>))}</div></div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs font-medium text-foreground-600 mb-1.5 block">PE 范围</label><div className="flex gap-2"><input value={peMin} onChange={e => setPeMin(e.target.value)} placeholder="不限" className={inputCls} /><span className="text-foreground-300 self-center">~</span><input value={peMax} onChange={e => setPeMax(e.target.value)} placeholder="30" className={inputCls} /></div></div>
              <div><label className="text-xs font-medium text-foreground-600 mb-1.5 block">市值（亿）</label><div className="flex gap-2"><input value={capMin} onChange={e => setCapMin(e.target.value)} placeholder="100" className={inputCls} /><span className="text-foreground-300 self-center">~</span><input value={capMax} onChange={e => setCapMax(e.target.value)} placeholder="不限" className={inputCls} /></div></div>
            </div>
            <div><label className="text-xs font-medium text-foreground-600 mb-1.5 block">最大持仓数</label><input type="number" value={maxHoldings} onChange={e => setMaxHoldings(Number(e.target.value))} className={inputCls} /></div>
          </div>
        )}
        {tab === "risk" && (
          <div className="space-y-4">
            <div className="flex items-center gap-2 mb-2"><span className="text-xs text-foreground-500">风格：{style}</span><button onClick={handleAIRecommendRisk} className="flex items-center gap-1 text-[10px] px-2 py-1 rounded-full bg-indigo-50 text-indigo-600 cursor-pointer"><i className="ri-ai-line" />AI 推荐参数</button></div>
            {[{ label: "单仓上限(%)", val: maxPos, set: setMaxPos }, { label: "集中度(%)", val: conc, set: setConc }, { label: "最低现金(%)", val: cashFloor, set: setCashFloor }, { label: "止盈(%)", val: profitPct, set: setProfitPct }, { label: "止损(%)", val: lossPct, set: setLossPct }, { label: "最大回撤(%)", val: maxDD, set: setMaxDD }].map(f => (
              <div key={f.label}><label className="text-xs font-medium text-foreground-600 mb-1.5 block">{f.label}</label><input type="number" value={f.val} onChange={e => f.set(Number(e.target.value))} className={inputCls} /></div>
            ))}
          </div>
        )}
        {tab === "prompt" && (
          <div className="space-y-4">
            <div className="flex items-center justify-between p-3 rounded-xl bg-primary-50 border border-primary-200/60">
              <span className="text-xs text-foreground-600"><i className="ri-ai-line text-primary-500" /> 使用系统推荐</span>
              <button onClick={() => setCustomPrompt(!customPrompt)} className={`text-[11px] px-3 py-1 rounded-full font-medium cursor-pointer ${customPrompt ? "bg-primary-500 text-white" : "bg-white text-foreground-500 border border-background-200/60"}`}>{customPrompt ? "自定义" : "推荐"}</button>
            </div>
            {[{ key: "debate", label: "决策官" }, { key: "fundamental", label: "基本面" }, { key: "technical", label: "技术面" }, { key: "sentiment", label: "情绪面" }, { key: "news", label: "新闻面" }, { key: "bull", label: "看多" }, { key: "bear", label: "看空" }].map(slot => (
              <div key={slot.key}>
                <div className="flex items-center justify-between mb-1"><label className="text-xs font-medium text-foreground-600">{slot.label}</label>{customPrompt && <button onClick={() => handleAIRewrite(slot.key, prompts[slot.key])} disabled={aiBusy === slot.key} className="flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-600 cursor-pointer disabled:opacity-40"><i className={`ri-ai-line ${aiBusy === slot.key ? "animate-spin" : ""}`} />AI</button>}</div>
                <textarea value={prompts[slot.key]} onChange={e => setPrompts(prev => ({ ...prev, [slot.key]: e.target.value }))} readOnly={!customPrompt} rows={2} className={`${inputCls} resize-none ${!customPrompt ? "opacity-60 cursor-default" : ""}`} />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
